import { Component, Input, OnInit } from '@angular/core';
import { PopupService } from '../../services/popup.service';
import { SchoolsPoiProperties } from '../../../menu/right-menu/layers-menu/education/education.service';
import { CrimeRateByYear } from '../../../menu/right-menu/layers-menu/points.service';
import { Feature } from 'geojson';
import {
  H3_RES5_LEVEL_LAYER,
  STATE_LEVEL_LAYER,
  LayerStoreService,
} from '../../services/layer-store.service';
import { SelectedCellService } from '../../services/selected-cell.service';
import { UserAccessService } from '../../../../user/access/user-access.service';
import {
  featuresToCompareToNationalPopup,
  LAND_AREA_FRACTION,
} from '../../../../shared/types/feature-data-type';
import { MapHttpService } from '../../services/map-http.service';
import { MapUrlService } from '../../services/map-url.service';
import {
  AsyncPipe,
  NgClass,
  NgIf,
  NgSwitch,
  NgSwitchCase,
} from '@angular/common';
import { LevelsPopupListComponent } from './layers-popup-list/levels-popup-list.component';
import { SortPropertiesPipe } from './sort-properties.pipe';
import { SchoolsPopupListComponent } from './schools-popup-list/schools-popup-list.component';
import { OriPopupListComponent } from './ori-popup-list/ori-popup-list.component';
import { MatTooltip } from '@angular/material/tooltip';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { IconButtonDirective } from '../../../../shared/directives/icon-button.directive';
import { FeatureTooltipComponent } from '../../../menu/right-menu/layers-menu/shared/feature-tooltip/feature-tooltip.component';
import { ModalService } from '../../../../shared/services/modal.service';
import { MapModalData } from '../../../../user/map-redirect-modal/map-redirect-modal.component';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { Router } from '@angular/router';
import centroid from '@turf/centroid';
import { registrationRequiredText } from '../../../../user/map-redirect-modal/map-redirect-modal-text';
import { salesForecastPath } from '../../../../sales-forecast/sales-forecast.routes-data';
import {
  USER_CATEGORY_KEY,
  UserCategory,
} from '../../../user-guide/user-guide.service';

export const DEFAULT_POPUP = 'DEFAULT_POPUP';
export const SCHOOLS_POPUP = 'SCHOOLS_POPUP';
export const ORI_POPUP = 'ORI_POPUP';
export const HOVER_POPUP = 'HOVER_POPUP';

export type PopupData = {
  type:
    | typeof DEFAULT_POPUP
    | typeof SCHOOLS_POPUP
    | typeof ORI_POPUP
    | typeof HOVER_POPUP;
  properties: Feature['properties'] | SchoolsPoiProperties | CrimeRateByYear;
  feature: MapboxGeoJSONFeature;
};

@Component({
  selector: 'app-map-popup',
  templateUrl: './map-popup.component.html',
  imports: [
    NgIf,
    AsyncPipe,
    NgClass,
    IconComponent,
    NgSwitch,
    LevelsPopupListComponent,
    NgSwitchCase,
    SortPropertiesPipe,
    SchoolsPopupListComponent,
    OriPopupListComponent,
    MatTooltip,
    IconButtonDirective,
    FeatureTooltipComponent,
  ],
  styleUrls: ['./map-popup.component.scss', '../../../../app.component.scss'],
})
export class MapPopupComponent implements OnInit {
  @Input() data!: PopupData;

  public title!: string;

  constructor(
    private selectedCellService: SelectedCellService,
    public accessService: UserAccessService,
    private http: MapHttpService,
    public popupService: PopupService,
    public layerStore: LayerStoreService,
    private urlService: MapUrlService,
    private modalService: ModalService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.data.type !== HOVER_POPUP) {
      this.popupService.handlePopupTitle();
      this.loadWrittenInStateFeatures(
        this.selectedCellService.getSelectedCellGeoId()!
      );
    }
  }

  public handleClose(): void {
    this.popupService.handleClose();
  }

  public toggleSeeMore(): void {
    this.popupService.toggleSeeMore();
  }

  public handleDownloadXlsx(): void {
    this.popupService.downloadXlsx();
  }

  // User category chosen in the onboarding guide or the layers-menu preset
  // chips — both write straight to USER_CATEGORY_KEY (see
  // MenuPresetService/UserGuideComponent), same storage ModalService reads
  // for its own plan recommendation. 'business' means the visitor identified
  // as scouting a site, so the action button below sends them to the
  // site-selection tool instead of AI chat (see handleActionButton).
  public get isBusinessCategory(): boolean {
    const category = localStorage.getItem(
      USER_CATEGORY_KEY
    ) as UserCategory | null;
    return category === 'business';
  }

  public handleActionButton(): void {
    if (this.isBusinessCategory) {
      this.goToSalesForecast();
      return;
    }

    const center = centroid(this.data.feature).geometry.coordinates;

    this.router.navigate(['/chat'],
      { queryParamsHandling: 'replace',
          queryParams: {
         ll: center[1] + ',' + center[0],
      }});

  }

  // Reuses the same title already shown at the top of this popup (a
  // reverse-geocoded place name, e.g. "33139, Miami Beach, FL") as the
  // address the sales-forecast page's search bar runs on load — see
  // AddressInputComponent.prefillAndSearch(). Falls back to the feature's
  // raw name if the async reverse-geocode hasn't resolved yet.
  private goToSalesForecast(): void {
    const address =
      this.popupService.popupTitle ||
      (this.data.feature.properties?.['name'] as string | undefined);

    this.router.navigate([`/${salesForecastPath('my-business')}`], {
      queryParams: address ? { address } : {},
    });
  }

  public goToSignUp(): void {
    this.router.navigate(['/sign-up']);
  }

  public openSignUpModal(): void  {
    this.modalService.requestAuth({
      headerText: 'One click unlocks 5-mile resolution',
      subHeaderText: registrationRequiredText.mainText,
    });
  }
  public openFullAccessModal(): void {
    this.modalService.openSubscriptionsModal('all_packages');
  }


  public handleShare(): void {
    this.urlService.copyUrlToClipboard();
  }

  public handleTooltipModalOpen(modalData: MapModalData): void {
    this.modalService.openModal(modalData);
  }

  private loadWrittenInStateFeatures(id: string) {
    this.http
      .getDataForSingleCell(
        id,
        this.selectedCellService.getCellType()!,
        // Add land area fraction to features list
        [...featuresToCompareToNationalPopup, LAND_AREA_FRACTION]
      )
      .subscribe((data) => {
        this.data.properties = { ...this.data.properties, ...data.object };
      });
  }

  protected readonly ORI_POPUP = ORI_POPUP;
  protected readonly SCHOOLS_POPUP = SCHOOLS_POPUP;
  protected readonly DEFAULT_POPUP = DEFAULT_POPUP;
  protected readonly HOVER_POPUP = HOVER_POPUP;
  protected readonly H3_RES5_LEVEL_LAYER = H3_RES5_LEVEL_LAYER;
  protected readonly STATE_LEVEL_LAYER = STATE_LEVEL_LAYER;
}
